import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useUpdateClient } from '@/hooks/useClientMutations'
import { errorMessage } from '@/helpers/logger'

/**
 * What the homes field holds once read: a count (or nothing recorded), or a
 * complaint. The `ok` flag is the discriminant, so a count of homes is never
 * mistaken for the message that refused one.
 */
type HomeCountParse =
  | { ok: true; value: number | null }
  | { ok: false; error: string }

/**
 * Read the homes field as typed.
 *
 * Done here rather than left to the schema, because a number input that
 * holds "two hundred" hands the schema NaN, and its message for that is about
 * types rather than about homes. Empty means "not recorded", which proposals
 * read as "do not show a price per home".
 */
export function parseHomeCount(text: string): HomeCountParse {
  const trimmed = text.trim().replace(/,/g, '')
  if (trimmed === '') return { ok: true, value: null }
  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, error: 'Enter a whole number of homes' }
  }
  const value = Number(trimmed)
  if (value < 1) return { ok: false, error: 'Enter at least one home' }
  if (value > 100_000) {
    return { ok: false, error: 'That is more homes than we can price per home' }
  }
  return { ok: true, value }
}

/**
 * The homes in an association, edited where it is shown.
 *
 * Inline beside the status rather than in a form, because it is one number
 * somebody looks up once (from the HOA's documents or the manager) and types
 * in, and the proposal's price per home appears the moment it is saved. It
 * saves on Enter or when the field loses focus, and only when it changed.
 */
export function HomeCountControl({
  clientId,
  homeCount,
}: {
  clientId: string
  homeCount: number | null
}) {
  const updateClient = useUpdateClient()
  const [draft, setDraft] = React.useState<string>(
    homeCount === null ? '' : String(homeCount),
  )
  const [error, setError] = React.useState<string | null>(null)
  const focused = React.useRef(false)

  // A save elsewhere (or a refetch) should show through when not mid-edit.
  // While the field is focused, the server value is what was there when they
  // started typing, and writing it back would throw away the keystrokes.
  React.useEffect(() => {
    if (focused.current) return
    setDraft(homeCount === null ? '' : String(homeCount))
    setError(null)
  }, [homeCount])

  async function commit(): Promise<void> {
    const parsed = parseHomeCount(draft)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    setError(null)
    if (parsed.value === homeCount) return
    try {
      await updateClient.mutateAsync({
        id: clientId,
        input: { homeCount: parsed.value },
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Could not save.'))
    }
  }

  return (
    <div className="space-y-1">
      <label
        className="flex items-center gap-1.5 text-xs text-muted-text"
        title="Used for the price per home on proposals."
      >
        Homes
        <input
          inputMode="numeric"
          className="h-7 w-20 rounded-md border border-default bg-surface px-1.5 text-xs text-primary-text tabular-nums transition-colors hover:border-strong focus-visible:outline-none disabled:opacity-60"
          value={draft}
          placeholder="Not set"
          disabled={updateClient.isPending}
          aria-invalid={error === null ? undefined : true}
          onChange={(event) => {
            setDraft(event.target.value)
          }}
          onFocus={() => {
            focused.current = true
          }}
          onBlur={() => {
            focused.current = false
            void commit()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void commit()
            }
          }}
        />
        {updateClient.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : null}
      </label>
      {error === null ? null : (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}
